import React, { useEffect, useState } from 'react';
import { Col, FormGroup, Label } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import apiProjects from 'api/projects';
import translations from 'services/translations/translations';
import CreatableDropdown from 'components/layout/forms/CreatableDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';

import { PAYMENT_TERM, useChoiceList } from 'services/offers/offerChoices';

import IOffer from './IOffer';

const OfferInvoicingInfo = ({
  formState,
  handleFormState,
  locked,
  handlePaymentTerm,
  paymentTerm,
}: {
  formState: IOffer;
  handleFormState: any;
  locked: boolean;
  handlePaymentTerm: any;
  paymentTerm: any;
}): JSX.Element | null => {
  const { t } = useTranslation([
    translations.offers,
    translations.common,
    translations.products,
  ]);

  // The VAT rates of the country of the department of the offer; a new offer is made
  // for the department of the user
  const currentUser = useSelector((state: any) => state.users.current);
  const country =
    formState.organizationUnit?.country ??
    (formState.organizationUnit === null
      ? currentUser?.organizationUnit?.country
      : null);
  const [taxRateTypes, setTaxRateTypes] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    apiProjects.taxRateTypes
      .get(country)
      .then((rates) => active && setTaxRateTypes(rates))
      .catch(() => active && setTaxRateTypes([]));
    return () => {
      active = false;
    };
  }, [country]);

  const offerIsLocked = () => locked;

  // The days suggested, as the admins have named them (e.g. 0: HETI); any days can be typed
  const paymentTerms = useChoiceList(PAYMENT_TERM);

  // An offer saved with the earlier VAT rate (24 %) keeps it until another rate is chosen
  const taxRates: string[] = (taxRateTypes ?? []).map(String);
  if (formState.taxRate !== '' && !taxRates.includes(formState.taxRate)) {
    taxRates.push(formState.taxRate);
  }

  // The locale keys use a decimal comma, because a dot separates keys in i18next
  const getTaxRateLabel = (taxRate: string) =>
    t(`${translations.offers}:taxRateTypes.${taxRate.replace('.', ',')}`);

  return (
    <>
      {/* The payment term and the VAT rate among the fields of the PDF. The electronic
          invoicing details are no longer asked here; values saved earlier stay in the
          offer. */}
      <Col className="offer-pdf-field">
        <SelectInputFieldControlled
          value={formState.taxRate}
          label={t(`${translations.offers}:Tax Rate`)}
          required
          id="taxRate"
          name="taxRate"
          onChange={handleFormState}
          options={[
            <option key="taxRateTypes-empty" value="">
              -
            </option>,
            ...taxRates.map((taxRate) => (
              <option key={`taxRateTypes-${taxRate}`} value={taxRate}>
                {getTaxRateLabel(taxRate)}
              </option>
            )),
          ]}
          disabled={offerIsLocked()}
        />
      </Col>
      {/* The payment term stands out in green */}
      <Col className="offer-pdf-field offer-pdf-field-key">
        <FormGroup>
          <Label className="label">
            {t(`${translations.offers}:Payment Term`)}
          </Label>
          <CreatableDropdown
            className="text-field search-field"
            placeholder={t(`${translations.offers}:Search or type`)}
            required
            name="paymentTerm"
            value={paymentTerm}
            onChange={handlePaymentTerm}
            options={paymentTerms}
            disabled={offerIsLocked()}
          />
        </FormGroup>
      </Col>
    </>
  );
};

export default OfferInvoicingInfo;
